import { useState } from "react";

export const mediaSrc = (u, w) => {
  if (!u) return "";
  const isApi = u.startsWith("/api/");
  const abs = isApi ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
  return w && isApi ? `${abs}?w=${w}` : abs;
};

export const orderedPhotos = (photos) => [...(photos || [])]
  .map((p) => (typeof p === "string" ? { url: p, caption: "" } : p))
  .filter((p) => p?.url)
  .sort((a, b) => (b.is_cover ? 1 : 0) - (a.is_cover ? 1 : 0) || (a.order ?? 0) - (b.order ?? 0));

/**
 * Shared photo gallery: hero image, prev/next, thumbnails, caption under the image.
 * `photos` accepts {url, caption, is_cover, order} objects or plain URLs. Admin-uploaded /api/media URLs get
 * responsive optimized variants; external URLs are used as-is.
 */
export function PhotoGallery({ photos, alt, hero = false, testid = "room-gallery" }) {
  const list = orderedPhotos(photos);
  const [i, setI] = useState(0);
  if (!list.length) return null;
  const idx = Math.min(i, list.length - 1);
  const cur = list[idx];
  const go = (d) => setI((v) => (v + d + list.length) % list.length);
  const btn = "absolute top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 text-[#1C1917] shadow hover:bg-white transition-colors";
  return (
    <div className={hero ? "" : "mt-4"} data-testid={testid}>
      <div className={`relative overflow-hidden bg-[#F5F5F4] ${hero ? "aspect-[16/9] md:aspect-[21/9] rounded-2xl border border-[#E7E5E4]" : "aspect-[16/10] rounded-xl"}`}>
        <img key={cur.url} src={mediaSrc(cur.url, 1600)} srcSet={cur.url.startsWith("/api/") ? `${mediaSrc(cur.url, 960)} 960w, ${mediaSrc(cur.url, 1600)} 1600w` : undefined}
          sizes={hero ? "(max-width: 768px) 100vw, 1280px" : "(max-width: 768px) 100vw, 640px"} alt={cur.caption || `${alt} photo ${idx + 1}`}
          decoding="async" fetchPriority={hero ? "high" : undefined} className="w-full h-full object-cover" data-testid={`${testid}-main`} />
        {list.length > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label="Previous photo" data-testid={`${testid}-prev`} className={btn + " left-3"}>‹</button>
            <button type="button" onClick={() => go(1)} aria-label="Next photo" data-testid={`${testid}-next`} className={btn + " right-3"}>›</button>
            <span className="absolute bottom-3 right-4 rounded-full bg-black/55 text-white text-[11px] px-2 py-0.5" data-testid={`${testid}-counter`}>{idx + 1} / {list.length}</span>
          </>
        )}
      </div>
      {cur.caption ? <p className="mt-2 text-sm text-[#44403C]" data-testid={`${testid}-caption`}>{cur.caption}</p> : null}
      {list.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid={`${testid}-thumbs`}>
          {list.map((p, k) => (
            <button type="button" key={p.url} onClick={() => setI(k)} aria-label={`Photo ${k + 1}`} data-testid={`${testid}-thumb-${k}`}
              className={`shrink-0 overflow-hidden rounded-lg border-2 ${hero ? "h-20 w-28" : "h-14 w-20"} ${k === idx ? "border-[#1C1917]" : "border-transparent opacity-80 hover:opacity-100"}`}>
              <img src={mediaSrc(p.url, 480)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
